import { IDEAS, IDEAS_COUNT, USERS } from '../constants/APIUrlConstants';
import axiosInstance from '../utils/Axios';
import {
  IdeaListResponse,
  IdeaDetailsResponse,
  InternalServerError,
  IdeaPayload,
  PostIdeaResponse,
  CountResponse,
  UnFollowResponse,
  FollowResponse,
} from '../types/types';
import { handleAxiosError } from './HandleError';

type GetAllIdeasResponse = IdeaListResponse | InternalServerError | Error;

export const getAllIdeasFromAPI = async (
  userId: number,
  currentPage: number,
  totalPages: number,
  sort?: string,
  following?: boolean | null,
  support_id?: number[],
  school_id?: number[],
  department_id?: number[],
  idea_owner_id?: number[],
): Promise<GetAllIdeasResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (userId !== null && userId !== undefined) {
      queryParams.append('user_id', userId.toString());
    }
    if (currentPage !== null && currentPage !== undefined) {
      queryParams.append('page', currentPage.toString());
    }
    if (totalPages !== null && totalPages !== undefined) {
      queryParams.append('page_size', totalPages.toString());
    }

    if (sort) queryParams.append('sort_order', sort);
    if (following !== null && following !== undefined) {
      queryParams.append('following', following.toString());
    }
    if (support_id && support_id.length > 0) {
      queryParams.append('support_id', support_id.join(','));
    }
    if (school_id && school_id.length > 0) {
      queryParams.append('school_id', school_id.join(','));
    }
    if (department_id && department_id.length > 0) {
      queryParams.append('department_id', department_id.join(','));
    }
    if (idea_owner_id && idea_owner_id.length > 0) {
      queryParams.append('idea_owner_id', idea_owner_id.join(','));
    }

    const response = await axiosInstance.get<IdeaListResponse>(
      `${IDEAS}?${queryParams.toString()}`,
    );

    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error) {
    console.error('failed to fetch the ideas:', error);
    return handleAxiosError(
      error,
      'an error occurred while fetching the ideas',
    );
  }
};

type GetIdeaDetailsResponse = IdeaDetailsResponse | InternalServerError | Error;

export const getIdeaDetails = async (
  ideaId: number,
  user_id: number | undefined,
): Promise<GetIdeaDetailsResponse> => {
  try {
    const response = await axiosInstance.get<IdeaDetailsResponse>(
      `${IDEAS}/${ideaId}`,
      {
        params: { user_id },
      },
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('Failed to fetch idea details:', error);
    return handleAxiosError(
      error,
      'an error occurred while fetching idea details.',
    );
  }
};

type GetIdeaCountResponse = CountResponse | InternalServerError | Error;

export const getIdeasCount = async (
  userId: number,
  following: boolean | null,
  support_id: number[],
  school_id: number[],
  department_id: number[],
  users: { id: number; name: string }[],
): Promise<GetIdeaCountResponse> => {
  try {
    const userIds = users.map((user) => user.id);
    const queryParams = new URLSearchParams({
      user_id: userId.toString(),
      ...(following !== null && { following: following.toString() }),
      ...(support_id.length > 0 && { support_id: support_id.join(',') }),
      ...(school_id.length > 0 && { school_id: school_id.join(',') }),
      ...(department_id.length > 0 && {
        department_id: department_id.join(','),
      }),
      ...(userIds.length !== 0 && {
        idea_owner_id: userIds.join(','),
      }),
    });

    const response = await axiosInstance.get<CountResponse>(
      `${IDEAS_COUNT}?${queryParams}`,
    );

    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to fetch ideas count:', error);
    return handleAxiosError(
      error,
      'an error occurred while failed to fetch ideas count',
    );
  }
};

export const postIdea = async (
  postIdeaPayload: IdeaPayload & { user_id: number },
): Promise<PostIdeaResponse | InternalServerError | Error> => {
  try {
    const response = await axiosInstance.post<PostIdeaResponse>(
      IDEAS,
      postIdeaPayload,
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else if (response.status === 201 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('error posting idea:', error);
    return handleAxiosError(error, 'an error occurred while post an idea');
  }
};

export const updateIdea = async (
  ideaId: number,
  data: IdeaPayload,
): Promise<IdeaDetailsResponse | InternalServerError | Error> => {
  try {
    const updateIdeaUrl = `${IDEAS}/${ideaId}`;

    const response = await axiosInstance.put<IdeaDetailsResponse>(
      updateIdeaUrl,
      data,
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to update idea:', error);
    return handleAxiosError(
      error,
      'an error occurred while failed to update idea',
    );
  }
};

export const fetchUserIdeas = async (
  userId: number,
  page: number,
  page_size: number,
): Promise<GetAllIdeasResponse> => {
  try {
    const response = await axiosInstance.get<IdeaListResponse>(
      `${USERS}/${userId}/ideas?page=${page}&page_size=${page_size}`,
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to fetch myideas:', error);
    return handleAxiosError(error, 'an error occurred while fetch myideas');
  }
};

type FollowAPIResponse = FollowResponse | InternalServerError | Error;

export const postFollowAPI = async (
  ideaId: number,
  userId: number,
): Promise<FollowAPIResponse> => {
  try {
    const response = await axiosInstance.post<FollowResponse>(
      `${IDEAS}/${ideaId}/follow`,
      {
        follower_user_id: userId,
      },
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to post follow idea:', error);
    return handleAxiosError(error, 'failed to post follow idea:');
  }
};

type UnFollowAPIResponse = UnFollowResponse | InternalServerError | Error;

export const deleteUnFollowAPI = async (
  ideaId: number,
  userId: number,
): Promise<UnFollowAPIResponse> => {
  try {
    const response = await axiosInstance.delete<UnFollowResponse>(
      `${IDEAS}/${ideaId}/unfollow`,
      {
        params: { follower_user_id: userId },
      },
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to post unfollow idea:', error);
    return handleAxiosError(error, 'failed to post unfollow idea:');
  }
};

type DeleteIdeaAPIResponse = UnFollowResponse | InternalServerError | Error;

export const deleteIdeaAPI = async (
  ideaId: number,
  userId: number,
): Promise<DeleteIdeaAPIResponse> => {
  try {
    const response = await axiosInstance.delete<UnFollowResponse>(
      `${IDEAS}/${ideaId}`,
      {
        params: { user_id: userId },
      },
    );
    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      return {
        status: response.status,
        code: 'INVALID_REQUEST',
        message: response.statusText,
      };
    }
  } catch (error: unknown) {
    console.error('failed to delete an idea:', error);
    return handleAxiosError(error, 'failed to delete an idea:');
  }
};
